import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'closed', label: 'Closed' },
];

const badgeCls = {
  new: 'bg-[#C7F464] text-[#17142A]',
  contacted: 'bg-[#EFEBFF] text-[#7047FF]',
  closed: 'bg-gray-100 text-gray-500',
};

function AdminEnterpriseInquiries() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/enterprise/inquiries', { params: { page: p } });
      const d = res.data || {};
      setRows(Array.isArray(d.data) ? d.data : []);
      setLastPage(d.last_page || 1);
      setTotal(d.total ?? (d.data ? d.data.length : 0));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load enterprise enquiries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [page, load]);

  const changeStatus = async (row, status) => {
    const previous = row.status;
    setRows((list) => list.map((r) => (r.id === row.id ? { ...r, status } : r)));
    try {
      await api.patch(`/admin/enterprise/inquiries/${row.id}/status`, { status });
    } catch (err) {
      setRows((list) => list.map((r) => (r.id === row.id ? { ...r, status: previous } : r)));
      setError(err.response?.data?.message || 'Could not update the status.');
    }
  };

  const visible = filter ? rows.filter((r) => (r.status || 'new') === filter) : rows;

  return (
    <div className="bg-white rounded-2xl border border-[#D9D5EE] shadow-sm p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="text-xl font-bold text-[#252438]">Enterprise enquiries</h3>
          <p className="text-sm text-gray-500 mt-1">
            Requests sent from the Corporate plan form. {total} in total.
          </p>
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-[#D9D5EE] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#7047FF]"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-600 text-sm px-4 py-3">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 py-8 text-center">Loading enquiries...</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No enquiries to show.</p>
      ) : (
        <div className="overflow-x-auto border border-[#EFEBFF] rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8F8FC] text-left text-[#252438]">
              <tr>
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Hiring needs</th>
                <th className="px-4 py-3 font-semibold">Message</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Received</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const status = r.status || 'new';
                const isOpen = openId === r.id;
                return (
                  <tr key={r.id} className="border-t border-[#EFEBFF] align-top">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-[#252438]">{r.company_name}</p>
                      {r.city && <p className="text-xs text-gray-500 mt-0.5">{r.city}</p>}
                    </td>

                    <td className="px-4 py-3">
                      {r.name && <p className="text-[#252438]">{r.name}</p>}
                      <a href={`mailto:${r.email}`} className="block text-[#7047FF] hover:underline break-all">
                        {r.email}
                      </a>
                      <a href={`tel:${r.phone}`} className="block text-gray-600 hover:underline">
                        {r.phone}
                      </a>
                    </td>

                    <td className="px-4 py-3 text-gray-600">
                      <p>Size: {r.company_size || '-'}</p>
                      <p>Jobs / month: {r.hiring_volume || '-'}</p>
                    </td>

                    <td className="px-4 py-3 max-w-xs">
                      {r.message ? (
                        <>
                          <p className={`text-gray-700 break-words ${isOpen ? '' : 'line-clamp-2'}`}>
                            {r.message}
                          </p>
                          {r.message.length > 80 && (
                            <button
                              type="button"
                              onClick={() => setOpenId(isOpen ? null : r.id)}
                              className="text-xs font-semibold text-[#7047FF] hover:underline mt-1"
                            >
                              {isOpen ? 'Show less' : 'Read more'}
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400">No message</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                    </td>

                    <td className="px-4 py-3">
                      <span className={`inline-block text-[11px] font-bold px-2.5 py-1 rounded-full mb-2 ${badgeCls[status] || badgeCls.closed}`}>
                        {STATUSES.find((s) => s.value === status)?.label || status}
                      </span>
                      <select
                        value={status}
                        onChange={(e) => changeStatus(r, e.target.value)}
                        className="block border border-[#D9D5EE] rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#7047FF]"
                        aria-label={`Change status for ${r.company_name}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#7047FF] text-[#7047FF] disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page} of {lastPage}</span>
          <button
            type="button"
            disabled={page >= lastPage || loading}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#7047FF] text-[#7047FF] disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminEnterpriseInquiries;